<template>
  <div class="ia-result" v-if="props.results">
    <div class="results-section">
      <div class="results-header" @click="toggleResults">
        <h3 class="results-title">{{ TEXT.response }}</h3>
        <button class="collapse-button" :class="{ collapsed: !isResultsExpanded }">
          <img :src="ICON" alt="Toggle" class="chevron-icon" />
        </button>
      </div>
      <div v-show="isResultsExpanded" class="results-content">
        <div class="markdown-content" v-html="renderedContent" ref="markdownContentRef"></div>
      </div>
    </div>

    <SearchIARelatedDocumentsComponent :results="props.results" />

    <SearchIAToolbarComponent
      :content="plainTextContentWithoutChunks"
      :html-content="htmlContentWithoutChunks"
      filename="resultado_busqueda"
      email-subject="Resultado de búsqueda avanzada"
    />

    <div class="legal-notice">
      <img :src="infoIcon" alt="Info" class="info-icon" />
      <span class="legal-text">{{ TEXT.legal }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import DOMPurify from 'dompurify'
import { useI18n } from 'vue-i18n-composable'
import { processMarkdown } from '@/helpers/markdown'
import { getChunkById, getChunkTooltipHTML } from '@/helpers/chunk'
import { useAdvanceSearchTooltip } from '@/modules/documentSearcher/composables/useAdvanceSearchTooltip'
import SearchIAToolbarComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIAToolbarComponent.vue'
import SearchIARelatedDocumentsComponent from '@/modules/documentSearcher/components/SearchAdvanceIA/components/SearchIARelatedDocumentsComponent.vue'

// DEFINE PROPS
const props = defineProps<{
  results: any
}>()

// TRANSLATIONS
const { t } = useI18n()

const TEXT = {
  response: t('views.search_ia_result.response') as string,
  legal: t('views.search_ia_result.legal') as string
}

// DATA
const ICON = require('@/assets/images/svg/icon-chevron-up.svg')
const infoIcon = require('@/assets/images/svg/icon-info.svg')
const isResultsExpanded = ref(true)
const CHUNK_ID_REGEX = /\[\s*CHUNK_ID\s*:\s*([^\]\s]+)\s*\]/g
// Usar el composable de tooltip
useAdvanceSearchTooltip()

// COMPUTED
// Crear un mapa de chunk_id a número autonumérico basado en los chunks que aparecen en generated_response
// Garantiza que cada chunk_id único tenga siempre el mismo número
const chunkIdMap = computed(() => {
  const map = new Map<string, number>()
  if (!props.results || !props.results.generated_response) {
    return map
  }

  // Extraer todos los chunk_ids que aparecen en generated_response
  const matches = [...props.results.generated_response.matchAll(CHUNK_ID_REGEX)]

  let counter = 1
  matches.forEach((match) => {
    const chunkId = match[1]
    if (chunkId && !map.has(chunkId)) {
      // Solo asignar número si el chunk_id no existe ya en el map
      map.set(chunkId, counter)
      counter++
    }
  })

  return map
})

const renderedContent = computed(() => {
  if (!props.results || !props.results.generated_response) {
    return ''
  }

  // Procesar el contenido principal como markdown
  const htmlContent = processMarkdown(props.results.generated_response)

  // Reemplazar los chunk IDs en el HTML generado
  const htmlWithChunks = htmlContent.replace(CHUNK_ID_REGEX, (match: string, chunkId: string) => {
    // Obtener el chunk correspondiente
    const chunk = getChunkById(props.results.retrieved_chunks, chunkId)
    if (!chunk) return match

    // Obtener el número del chunk desde el mapa
    const chunkNumber = chunkIdMap.value.get(chunkId) || '?'

    // Crear HTML para el tooltip con el número en lugar del icono
    return `<span class="tooltip-wrapper">
      <button
        class="link-btn chunk-reference-btn"
        data-tooltip="${encodeURIComponent(getChunkTooltipHTML(chunk))}"
        data-chunk-id="${chunkId}"
      >
        <span class="chunk-number">${chunkNumber}</span>
      </button>
    </span>`
  })

  // Sanitizar el HTML final con los botones de chunks
  return DOMPurify.sanitize(htmlWithChunks, {
    ADD_ATTR: ['data-tooltip', 'data-chunk-id', 'style'],
    ADD_TAGS: ['button', 'span']
  })
})

// Contenido sin chunks para las acciones de la toolbar (copiar, descargar, email, imprimir)
const htmlContentWithoutChunks = computed(() => {
  if (!props.results || !props.results.generated_response) {
    return ''
  }
  const rawWithoutChunks = props.results.generated_response.replace(CHUNK_ID_REGEX, '')
  return DOMPurify.sanitize(processMarkdown(rawWithoutChunks))
})

const plainTextContentWithoutChunks = computed(() => {
  if (!htmlContentWithoutChunks.value) {
    return ''
  }
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = htmlContentWithoutChunks.value
  return tempDiv.textContent || tempDiv.innerText || ''
})

// METHODS
const toggleResults = (): void => {
  isResultsExpanded.value = !isResultsExpanded.value
}
</script>

<style lang="scss" scoped>
.ia-result {
  font-family: $inter-font;
  width: 100%;
  padding: $spacing-xs;
  margin: 0px 10px 10px 10px;
  font-size: 1rem;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
}

.results-section {
  width: 100%;
  border: 1px solid $neutral-grey-300;
  border-radius: $cornerRadius-lg;
  overflow: hidden;
  background: $neutral-white-000;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-sm $spacing-md;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.results-title {
  font-family: $inter-font;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
  color: $neutral-grey-800;
}

.collapse-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;

  &.collapsed {
    transform: rotate(180deg);
  }

  .chevron-icon {
    width: 16px;
    height: 16px;
    color: $neutral-grey-600;
  }
}

.results-content {
  max-height: 600px;
  overflow-y: auto;
  padding: $spacing-md;
  font-size: 14px !important;

  /* Personalización del scroll */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: $main-100;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: $main-300;
    border-radius: 4px;
  }

  /* Ocultar las flechas del scroll */
  &::-webkit-scrollbar-button {
    display: none;
  }
}

.related-documents {
  font-family: $inter-font;
  color: $neutral-grey-800;
  margin: $spacing-xxs 0 $spacing-xxs 0;
  padding-bottom: $spacing-xxxs;
  display: flex;
  align-items: center;
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 26px;
}

:deep(.markdown-content) {
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 16px;
    font-weight: 600;
    line-height: 1;
  }

  h1 {
    font-size: 18px;
  }
  h2 {
    font-size: 17px;
  }
  h3 {
    font-size: 16px;
  }

  p {
    margin-top: 0;
    margin-bottom: 16px;
    line-height: 1;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
  }

  ul,
  ol {
    padding-left: 2em;
    margin-top: 0;
    margin-bottom: 16px;
  }

  li {
    margin-bottom: 8px;
  }

  .tooltip-wrapper {
    display: inline-flex;
    align-items: center;
    margin: 0 0px;
    background-color: $neutral-white-000;
  }

  .chunk-reference-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    min-width: 16px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background-color: $main-500;
    cursor: pointer;
    transition: all 0.2s;
    vertical-align: middle;
  }

  .chunk-number {
    font-family: $inter-font;
    font-size: 10px;
    font-weight: 600;
    color: $neutral-white-000;
    line-height: 1;
    margin: 0;
    padding: 0;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 14px;
    margin-bottom: 24px;
  }

  th {
    font-weight: 500;
    font-size: 15px;
    text-align: left;
    padding: 8px 12px;
    border: 1px solid #e0ddd6;
  }

  td {
    vertical-align: top;
    padding: 8px 12px;
    color: #1a1a1a;
    border: 1px solid #e0ddd6;
  }

  tr:nth-child(odd) td {
    background-color: #ffffff;
  }

  tr:nth-child(even) td {
    background-color: #fafaf8;
  }
}

// Nuevos estilos para la sección de archivos
.source-files-title {
  @include milano-regular-16;
  margin-bottom: $spacing-sm;
  color: $neutral-grey-800;
}

.source-files-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: $spacing-sm;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

/* Estilos para los iconos de archivo */
:deep(.e-filemanager) {
  @include file-icons('e-filemanager');
}

/* Ajustes específicos para los iconos en los botones */
:deep(.lf-label-button__text) {
  [class^='e-fe-'] {
    margin-right: $spacing-xs;
    width: 16px;
    height: 16px;
    display: inline-block;
    vertical-align: middle;
  }
}

/* Estilos para el aviso legal */
.legal-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: $spacing-sm $spacing-nule;
  border-radius: $cornerRadius-md;
  font-family: $inter-font;
  width: 100%;
  box-sizing: border-box;
}

.info-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  color: $neutral-grey-900;
}

.legal-text {
  font-family: $inter-font;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  color: $neutral-grey-900;
}

/* Estilos para los botones de archivo con truncamiento */
.file-button {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  font-family: $inter-font;
  justify-content: flex-start;
  border-radius: 12px;
  text-transform: none;
  background: $neutral-white-000;
  border: 1px solid $neutral-grey-300;
  box-shadow: none;
  min-height: 38px;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  box-sizing: border-box;
  overflow: hidden;

  :deep(.lf-label-button__content) {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
  }

  :deep(.lf-label-button__text) {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    align-items: center;
    box-sizing: border-box;

    [class^='e-fe-'] {
      flex-shrink: 0;
      margin-right: $spacing-xs;
      width: 16px;
      height: 16px;
      display: inline-block;
      vertical-align: middle;
    }

    span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      min-width: 0;
      flex: 1;
    }
  }
}
</style>

<style lang="scss">
/* Estilos globales para el tooltip */
.custom-tooltip {
  z-index: 9999;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  padding: 12px;
  max-width: 500px !important;
  max-height: 500px;
  overflow: hidden;
  font-size: 12px;
  color: #333;
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  position: relative;
  box-sizing: border-box;
  --arrow-left: 50%;
}

.custom-tooltip::after {
  content: '';
  position: absolute;
  left: var(--arrow-left);
  margin-left: -10px;
  border-width: 10px;
  border-style: solid;
}

.custom-tooltip.tooltip-above::after {
  bottom: -20px;
  border-color: white transparent transparent transparent;
}

.custom-tooltip.tooltip-below::after {
  top: -20px;
  border-color: transparent transparent white transparent;
}

.chunk-tooltip {
  font-family: $inter-font;
  max-height: calc(100% - 60px);
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  flex: 1;
  padding-bottom: 50px;
  margin-right: -6px;
  padding-right: 6px;
}

.chunk-tooltip p {
  margin: 0 0 8px;
}

.chunk-tooltip .chunk-content {
  padding: 8px;
  border-radius: 4px;
  margin-top: 4px;
  white-space: pre-line;
  font-family: $inter-font;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 320px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Incluir los estilos de iconos de archivo globalmente */
@include file-icons('source-files-grid');

.source-files-grid .e-fe-icon::before {
  width: 21px;
}

/* Estilos para el botón "Ir a documento" */
.chunk-tooltip .document-button-container {
  position: absolute;
  bottom: 12px;
  right: 12px;
  margin: 0;
  padding: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
}

.chunk-tooltip .go-to-document {
  display: inline-block;
  padding: 8px 16px;
  background-color: $neutral-white-000;
  color: $main-1000;
  border: 1px solid $main-1000;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);

  &:hover {
    background-color: rgba($main-1000, 0.05);
    text-decoration: none;
  }

  &:active {
    background-color: rgba($main-1000, 0.1);
  }
}

/* Personalización del scroll*/
.chunk-tooltip::-webkit-scrollbar {
  width: 6px;
}

.chunk-tooltip::-webkit-scrollbar-track {
  background: $main-100;
  border-radius: 4px;
}

.chunk-tooltip::-webkit-scrollbar-thumb {
  background: $main-300;
  border-radius: 4px;
}

/* Aplicar los mismos estilos al contenido del chunk */
.chunk-tooltip .chunk-content::-webkit-scrollbar {
  width: 6px;
}

.chunk-tooltip .chunk-content::-webkit-scrollbar-track {
  background: $main-100;
  border-radius: 4px;
}

.chunk-tooltip .chunk-content::-webkit-scrollbar-thumb {
  background: $main-300;
  border-radius: 4px;
}
</style>
